import React, { useState, useMemo } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const DischargeAdviceList = ({ records = [], onAdd, onEdit, onDelete }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter Logic
  const filteredRecords = useMemo(
    () =>
      records.filter((r) =>
        Object.values(r).some((v) =>
          String(v ?? "").toLowerCase().includes(filterText.toLowerCase())
        )
      ),
    [records, filterText]
  );

  const totalRows = filteredRecords.length;
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // Export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Discharge Advice Records", 14, 10);
    const tableColumn = [
      "#",
      "Client Name",
      "Assigned PRP",
      "Diagnosis",
      "Reason",
      "Discharge Date",
      "Approved By",
    ];
    const tableRows = filteredRecords.map((r, index) => [
      index + 1,
      r.client_name,
      r.assigned_prp,
      r.psychiatric_diagnosis,
      r.discharge_reason,
      r.discharge_date,
      r.approved_by,
    ]);
    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
    doc.save("discharge_advices.pdf");
  };

  // Custom Pagination
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px", fontSize: "0.875rem" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // Columns
  const columns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "80px",
    },
    { name: "Client Name", selector: (r) => r.client_name, sortable: true },
    { name: "Assigned PRP", selector: (r) => r.assigned_prp, sortable: true },
    { name: "Diagnosis", selector: (r) => r.psychiatric_diagnosis, sortable: true },
    { name: "Reason", selector: (r) => r.discharge_reason, sortable: true },
    { name: "Discharge Date", selector: (r) => r.discharge_date, sortable: true },
    { name: "Approved By", selector: (r) => r.approved_by, sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          {onEdit && (
            <button
              className="btn btn-sm btn-outline-primary"
              title="Edit"
              onClick={() => onEdit(row.id)}
            >
              <i className="ri-edit-line"></i>
            </button>
          )}
          <button
            className="btn btn-sm btn-outline-danger"
            title="Delete"
            onClick={() => onDelete(row.id)}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
      width: "160px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filteredRecords}
                  filename="discharge_advices.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search discharge advices..."
            className="form-control form-control-sm"
            style={{ width: "250px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Center Info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} records
        </h6>

        {/* Add Button */}
        <div>
          {onAdd && (
            <button className="btn btn-sm btn-primary" onClick={onAdd}>
              <i className="ri-add-line me-1"></i> Add New
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredRecords}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={
            <div className="p-3 text-muted text-center">
              <i className="ri-file-list-3-line fs-1 text-muted d-block mb-2"></i>
              No discharge advice records found
            </div>
          }
        />
      </div>
    </div>
  );
};

export default DischargeAdviceList;